import React from 'react';
import { Box, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { EDIT_OBJECT_TOKENS } from './editObjectTokens';

export interface AddMoreTileProps {
  onClick: () => void;
  disabled?: boolean;
}

/**
 * Mock's `.add-img-card`: a dashed-border square that sits as the last
 * cell of the image grid. Clicking it opens the file picker (the dropzone
 * above the grid is the primary affordance — this is the inline shortcut
 * the mock includes for parity with the populated state).
 */
export const AddMoreTile: React.FC<AddMoreTileProps> = ({ onClick, disabled = false }) => (
  <Box
    role="button"
    tabIndex={disabled ? -1 : 0}
    aria-disabled={disabled}
    onClick={disabled ? undefined : onClick}
    onKeyDown={(e) => {
      if (disabled) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    }}
    data-testid="edit-object-add-more-tile"
    sx={{
      width: '100%',
      aspectRatio: '1 / 1',
      bgcolor: EDIT_OBJECT_TOKENS.color.paper,
      border: `1.5px dashed ${EDIT_OBJECT_TOKENS.color.line}`,
      borderRadius: `${EDIT_OBJECT_TOKENS.radius.sm}px`,
      display: 'grid',
      placeItems: 'center',
      color: EDIT_OBJECT_TOKENS.color.aliRed700,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      transition: 'border-color 180ms ease, background 180ms ease',
      '&:hover': disabled
        ? undefined
        : {
            borderColor: EDIT_OBJECT_TOKENS.color.aliRed700,
            bgcolor: EDIT_OBJECT_TOKENS.color.aliRed050,
          },
      '&:focus-visible': {
        outline: `2px solid ${EDIT_OBJECT_TOKENS.color.aliRed700}`,
        outlineOffset: 2,
      },
    }}
  >
    <Box sx={{ textAlign: 'center', fontSize: 12, fontWeight: 600 }}>
      <AddIcon sx={{ display: 'block', mx: 'auto', mb: 0.5, fontSize: 28 }} />
      Add more
    </Box>
  </Box>
);

export default AddMoreTile;
